import { useCallback, useEffect, useState } from "react";
import type { FailureCode } from "@/modules/shared/failure";
import { call, type ApiResult } from "./api";

/**
 * Reading from the API, for a screen (R22 T12).
 *
 * **The loading flag is derived, never set.** A screen holds the answer to one
 * question, and the question is the path; when the path changes the answer it
 * is holding is stale by definition, so `loading` is `answer.key !== path`
 * rather than a boolean somebody has to remember to flip. Two things fall out
 * of that and both are bugs this avoids: a fetch that resolves after the user
 * has moved on cannot overwrite the newer one, and a screen can never get stuck
 * showing a skeleton because an early return skipped a `setLoading(false)`.
 *
 * **`data` survives a refresh.** When a re-read is in flight the previous
 * answer is still there, so a filter change re-paints the list rather than
 * blanking it. Screens that want the skeleton ask for `status === "loading" &&
 * !data`, which is the honest first-load condition.
 *
 * **A refusal is a code, not a sentence**, exactly as everywhere else: the
 * screen decides what "not_found" looks like, and `not_configured` is how the
 * client tells a build nobody pointed at a database from a real outage.
 */
export interface Query<T> {
  readonly data: T | null;
  readonly loading: boolean;
  /** The refusal code, when the last completed read failed. */
  readonly failure: FailureCode | null;
  /** Re-read the same path, for after a command that changes what it returns. */
  readonly reload: () => void;
}

export function useQuery<T>(
  /** The path under `/api/web/v1`, or null to ask nothing at all. */
  path: string | null,
  options: { anonymous?: boolean } = {},
): Query<T> {
  const { anonymous } = options;
  const [nonce, setNonce] = useState(0);
  const [answer, setAnswer] = useState<{
    key: string;
    result: ApiResult<T>;
  } | null>(null);

  const key = path === null ? null : `${nonce}:${path}`;

  useEffect(() => {
    if (path === null || key === null) return;
    let cancelled = false;
    void call<T>(path, { anonymous }).then((result) => {
      if (!cancelled) setAnswer({ key, result });
    });
    return () => {
      cancelled = true;
    };
  }, [key, path, anonymous]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // Only an answer to *this* question counts. An older one is kept as `data`
  // so the screen does not blank while re-reading, but it is never treated as
  // current, and a failure it carried is not shown against the new question.
  const current = answer?.key === key ? answer.result : null;

  return {
    data: current?.ok
      ? current.value
      : answer?.result.ok
        ? answer.result.value
        : null,
    loading: current === null && path !== null,
    failure: current && !current.ok ? current.failure.code : null,
    reload,
  };
}
